import { motion, AnimatePresence } from "framer-motion";
import type { BreathingPhase } from "@/hooks/useBreathingExercise";

interface Props {
  phase: BreathingPhase;
  countdown: number;
  isRunning: boolean;
  isComplete: boolean;
  reduceMotion: boolean;
  durations: { inhale: number; hold: number; exhale: number; rest: number };
}

const phaseLabel: Record<BreathingPhase, string> = {
  inhale: "Inhale",
  hold: "Hold",
  exhale: "Exhale",
  rest: "Rest",
};

function getTargetScale(phase: BreathingPhase): number {
  switch (phase) {
    case "inhale": return 1;       // grows TO full
    case "hold":   return 1;       // stays large
    case "exhale": return 0.65;    // shrinks
    case "rest":   return 0.65;    // stays small
  }
}

function getTargetOpacity(phase: BreathingPhase): number {
  switch (phase) {
    case "inhale": return 0.9;
    case "hold":   return 0.9;
    case "exhale": return 0.4;
    case "rest":   return 0.4;
  }
}

export default function BreathingCircle({
  phase,
  countdown,
  isRunning,
  isComplete,
  reduceMotion,
  durations,
}: Props) {
  const scale = isRunning || isComplete ? getTargetScale(phase) : 0.65;
  const fillOpacity = isRunning || isComplete ? getTargetOpacity(phase) : 0.4;
  const duration = reduceMotion
    ? 0
    : phase === "inhale"
    ? durations.inhale
    : phase === "exhale"
    ? durations.exhale
    : 0.4;

  if (reduceMotion) {
    return (
      <div
        className="relative flex items-center justify-center mx-auto"
        style={{ width: 200, height: 200 }}
        role="img"
        aria-label={`${phaseLabel[phase]} ${countdown} seconds`}
      >
        <div
          className="rounded-full border-4 flex flex-col items-center justify-center"
          style={{
            width: 200,
            height: 200,
            borderColor: "#2D5A3D",
            backgroundColor: "#E1F5EE",
          }}
        >
          <span className="text-xl font-medium" style={{ color: "#2D5A3D" }}>
            {isComplete ? "Complete" : phaseLabel[phase]}
          </span>
          {!isComplete && (
            <span className="text-3xl font-light mt-1" style={{ color: "#2D5A3D" }}>
              {countdown}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative flex items-center justify-center mx-auto"
      style={{ width: 200, height: 200 }}
      role="img"
      aria-label={`${phaseLabel[phase]} ${countdown} seconds`}
    >
      <motion.div
        className="absolute rounded-full border-4"
        style={{
          width: 200,
          height: 200,
          borderColor: "#2D5A3D",
          backgroundColor: "#E1F5EE",
        }}
        animate={{
          scale,
          opacity: fillOpacity,
        }}
        transition={{
          duration,
          ease: "easeInOut",
        }}
      />

      <div className="relative z-10 flex flex-col items-center justify-center text-center pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.span
            key={isComplete ? "complete" : phase}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.4 }}
            className="text-xl font-medium tracking-wide"
            style={{ color: "#2D5A3D", fontFamily: "ui-rounded, system-ui, sans-serif" }}
          >
            {isComplete ? "Complete" : phaseLabel[phase]}
          </motion.span>
        </AnimatePresence>
        {!isComplete && isRunning && (
          <motion.span
            key={`${phase}-${countdown}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            className="text-3xl font-light mt-1"
            style={{ color: "#2D5A3D" }}
          >
            {countdown}
          </motion.span>
        )}
      </div>
    </div>
  );
}
